// Trust Admin — Vendor Empanelment dashboard.
// Route: /admin/vendors. Tabs by status + approve/reject/reopen + delete + export CSV.

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Trash2, Building2, FileText, Loader2, Eye, Download, KeyRound, Copy, RefreshCw, ShieldCheck } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "Pending", label: "Pending" },
  { key: "Under Review", label: "Under Review" },
  { key: "Approved", label: "Approved" },
  { key: "Rejected", label: "Rejected" },
];

function fmt(d) { try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d || ""; } }

function csvCell(v) {
  const s = String(v == null ? "" : v).replace(/"/g, '""');
  return `"${s}"`;
}

export default function VendorEmpanelment() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "editor", "infrastructure"].includes(user?.role);
  const [tab, setTab] = useState("Pending");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [creds, setCreds] = useState(null); // { has_login, email, initial_password, must_change_password, last_login_at, is_active }
  const [credsLoading, setCredsLoading] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vendors", { params: { status: tab } });
      setList(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  // Load vendor portal credentials when opening an Approved vendor's detail modal.
  useEffect(() => {
    if (!detail || detail.status !== "Approved") { setCreds(null); return; }
    let cancel = false;
    (async () => {
      setCredsLoading(true);
      try {
        const { data } = await api.get(`/admin/vendors/${detail.id}/portal-credentials`);
        if (!cancel) setCreds(data);
      } catch (err) {
        if (!cancel) setCreds(null);
      } finally {
        if (!cancel) setCredsLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [detail]);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Could not copy — please copy manually"); }
  };

  const regenerateCreds = async () => {
    if (!detail || !window.confirm("Reset this vendor's portal password? The new temporary password will be emailed to them and shown here once.")) return;
    setRegenBusy(true);
    try {
      const { data } = await api.post(`/admin/vendors/${detail.id}/portal-credentials/regenerate`);
      setCreds({
        has_login: true,
        email: data.email,
        initial_password: data.initial_password,
        must_change_password: true,
        last_login_at: creds?.last_login_at || "",
        is_active: true,
      });
      toast.success("New password generated and emailed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate password");
    } finally { setRegenBusy(false); }
  };

  const changeStatus = async (v, status) => {
    setBusyId(v.id);
    try {
      await api.patch(`/admin/vendors/${v.id}/status`, { status, admin_note: v.admin_note || "" });
      toast.success(`Marked ${status}`);
      setDetail(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setBusyId(""); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/vendors/${confirmDelete.id}`);
      toast.success("Submission deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const exportCsv = () => {
    if (!list.length) { toast.error("Nothing to export"); return; }
    const headers = ["Submitted", "Status", "Firm", "Type", "Company Type", "Company Categories", "Year", "State", "City", "PIN", "Email", "Mobile", "PAN", "GST", "Employees", "Annual Turnover"];
    const rows = list.map((v) => {
      const cats = (v.company_categories && v.company_categories.length ? v.company_categories : (v.company_category ? [v.company_category] : [])).join("; ");
      return [fmt(v.created_at), v.status, v.applicant_firm_name, v.applicant_type, v.company_type, cats, v.year_of_establishment, v.state, v.city, v.pin_code, v.email, v.mobile, v.pan_number, v.gst_number, v.employees_range, v.annual_turnover || ""];
    });
    const csv = [headers.map(csvCell).join(","), ...rows.map((r) => r.map(csvCell).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vendor-empanelment-${tab.replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const counts = useMemo(() => ({ [tab]: list.length }), [tab, list]);

  return (
    <div className="space-y-5" data-testid="adm-vendors">
      <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
            <h1 className="font-serif text-2xl text-[#1c1a17] flex items-center gap-2"><Building2 size={18} /> Vendor Empanelment</h1>
            <div className="text-sm text-[#6b655b] mt-0.5">Review vendor registration submissions received from /vendors/register.</div>
          </div>
          <button type="button" onClick={exportCsv} data-testid="adm-vendors-export" className="inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1a3125] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm">
            <Download size={12} /> Export CSV
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-b border-[#e5e0d8]">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`adm-vendors-tab-${t.key.replace(/\s+/g, "-").toLowerCase()}`} className={`text-[11px] uppercase tracking-wider font-bold px-4 py-2.5 transition-colors ${tab === t.key ? "bg-[#234333] text-white" : "text-[#6b655b] hover:bg-[#f5f1e8]"}`}>
              {t.label}{tab === t.key && ` (${counts[tab] || 0})`}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="text-sm text-[#6b655b] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
          ) : list.length === 0 ? (
            <div className="bg-[#f5f1e8] rounded-sm p-8 text-center text-sm text-[#6b655b]">No {tab.toLowerCase()} submissions.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#6b655b] border-b border-[#e5e0d8]">
                    <th className="py-2 pr-3">Submitted</th>
                    <th className="py-2 pr-3">Firm</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Company</th>
                    <th className="py-2 pr-3">City</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((v) => (
                    <tr key={v.id} className="border-b border-[#f0ebe0] hover:bg-[#fbf8f1]" data-testid={`adm-vendor-row-${v.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{fmt(v.created_at)}</td>
                      <td className="py-2 pr-3">
                        <div className="font-semibold text-[#1c1a17]">{v.applicant_firm_name}</div>
                        <div className="text-[10px] text-[#6b655b]">{v.pan_number} · {v.year_of_establishment}</div>
                      </td>
                      <td className="py-2 pr-3">{v.applicant_type}</td>
                      <td className="py-2 pr-3">
                        <div>{v.company_type}</div>
                        <div className="text-[10px] text-[#6b655b]">{((v.company_categories && v.company_categories.length ? v.company_categories : (v.company_category ? [v.company_category] : []))).join(", ") || "—"}</div>
                      </td>
                      <td className="py-2 pr-3">{v.city}, {v.state}</td>
                      <td className="py-2 pr-3">
                        <div>{v.email}</div>
                        <div className="text-[10px] text-[#6b655b]">{v.mobile}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          <button type="button" onClick={() => { setDetail(v); setNote(v.admin_note || ""); }} data-testid={`adm-vendor-view-${v.id}`} className="inline-flex items-center gap-1 bg-white border border-[#e5e0d8] hover:border-[#234333] text-[#234333] px-2 py-1 rounded-sm text-[10px] font-bold uppercase">
                            <Eye size={11} /> View
                          </button>
                          {canEdit && (
                            <button type="button" onClick={() => setConfirmDelete(v)} data-testid={`adm-vendor-delete-${v.id}`} className="inline-flex items-center gap-1 border border-red-300 text-red-700 hover:bg-red-50 px-2 py-1 rounded-sm text-[10px] font-bold uppercase">
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4" onClick={() => setDetail(null)} data-testid="adm-vendor-detail">
          <div className="bg-white rounded-sm shadow-2xl max-w-4xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#234333] text-white px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#c78e42]">{detail.status}</div>
                <div className="font-serif text-lg">{detail.applicant_firm_name}</div>
              </div>
              <button type="button" onClick={() => setDetail(null)} className="text-white/80 hover:text-white" data-testid="adm-vendor-detail-close">×</button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-5 text-xs">
              <Section title="Applicant">
                <Row k="Type" v={detail.applicant_type} />
                <Row k="Registered Address" v={detail.registered_address} />
                <Row k="Communication Address" v={detail.communication_address} />
                <Row k="State / City / PIN" v={`${detail.state} / ${detail.city} / ${detail.pin_code}`} />
                <Row k="Mobile" v={detail.mobile} />
                <Row k="Office Phone" v={detail.office_phone} />
                <Row k="Email" v={detail.email} />
                <Row k="Website" v={detail.website} />
                <Row k="Branch Office" v={detail.branch_office_details} />
              </Section>
              <Section title="Organization">
                <Row k="Type / Category" v={`${detail.company_type} / ${((detail.company_categories && detail.company_categories.length ? detail.company_categories : (detail.company_category ? [detail.company_category] : []))).join(", ") || "—"}`} />
                <Row k="Established" v={detail.year_of_establishment} />
                <Row k="Reg No. / Date" v={`${detail.registration_number} — ${detail.registration_date}`} />
                <Row k="PAN / GST" v={`${detail.pan_number} / ${detail.gst_number || "—"}`} />
                <Row k="CoA" v={detail.coa_registration} />
                <Row k="IE Membership" v={detail.ie_membership} />
                <Row k="Other Reg." v={detail.other_professional_registration} />
                <Row k="Employees" v={detail.employees_range} />
                <Row k="Annual Turnover" v={detail.annual_turnover} />
                <Row k="Description" v={detail.long_description} />
              </Section>
              <Section title="Portal Access" full>
                {detail.status !== "Approved" ? (
                  <div className="text-[11px] text-[#6b655b] italic">Portal credentials are generated automatically when the vendor is Approved.</div>
                ) : credsLoading ? (
                  <div className="text-[11px] text-[#6b655b] flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading credentials…</div>
                ) : !creds || !creds.has_login ? (
                  <div className="text-[11px] text-[#6b655b] italic">No portal login found for this vendor.</div>
                ) : (
                  <div className="bg-[#f7f3ea] border border-[#e5e0d8] rounded-sm p-3 space-y-2" data-testid="adm-vendor-portal-creds">
                    <div className="flex items-center gap-2">
                      <KeyRound size={14} className="text-[#234333]" />
                      <div className="text-[11px] uppercase tracking-wider font-bold text-[#234333]">Vendor Portal Login</div>
                      {creds.must_change_password ? (
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-wider bg-[#c78e42]/15 text-[#8a5a1a] px-2 py-0.5 rounded-sm">Temp password active</span>
                      ) : (
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 px-2 py-0.5 rounded-sm inline-flex items-center gap-1"><ShieldCheck size={11} /> Vendor changed password</span>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-2 text-xs">
                      <div className="bg-white border border-[#e5e0d8] rounded-sm px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-[#6b655b] font-bold">Username / Email</div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-[#1c1a17] truncate" data-testid="adm-vendor-portal-email">{creds.email}</div>
                          <button type="button" onClick={() => copyToClipboard(creds.email, "Email")} className="text-[#234333] hover:text-[#c78e42]" title="Copy email"><Copy size={12} /></button>
                        </div>
                      </div>
                      <div className="bg-white border border-[#e5e0d8] rounded-sm px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-[#6b655b] font-bold">Temporary Password</div>
                        {creds.initial_password ? (
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-mono text-[#1c1a17] tracking-widest text-sm" data-testid="adm-vendor-portal-password">{creds.initial_password}</div>
                            <button type="button" onClick={() => copyToClipboard(creds.initial_password, "Password")} className="text-[#234333] hover:text-[#c78e42]" title="Copy password"><Copy size={12} /></button>
                          </div>
                        ) : (
                          <div className="text-[#6b655b] italic text-[11px]" data-testid="adm-vendor-portal-password-hidden">
                            Password already changed by vendor — no longer visible.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                      <div className="text-[10px] text-[#6b655b]">
                        Last login: <span className="font-semibold text-[#1c1a17]">{creds.last_login_at ? new Date(creds.last_login_at).toLocaleString() : "Never"}</span>
                      </div>
                      {canEdit && (
                        <button type="button" onClick={regenerateCreds} disabled={regenBusy} data-testid="adm-vendor-regen-password" className="inline-flex items-center gap-1 bg-[#c78e42] hover:bg-[#a5875a] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm disabled:opacity-60">
                          {regenBusy ? <><Loader2 size={11} className="animate-spin" /> Resetting…</> : <><RefreshCw size={11} /> Reset Password</>}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </Section>
              <Section title="Portal Documents (uploaded by vendor)" full>
                <div className="mt-2 mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#6b655b] font-bold mb-1">Submitted at Registration</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Attach label="PAN Card" url={detail.pan_card_url} />
                    <Attach label={detail.gst_is_declaration ? "GST · Declaration" : "GST Certificate"} url={detail.gst_certificate_url} />
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#6b655b] font-bold mb-1">Uploaded via Vendor Portal</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Attach label="Cancelled Cheque" url={(detail.portal_cancelled_cheque || {}).url} />
                  <Attach label="Company / MSME Reg" url={(detail.portal_company_registration || {}).url} />
                </div>
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#6b655b] font-bold mb-1">Project Completion Certificates</div>
                  {(detail.portal_project_certificates || []).length === 0 ? (
                    <div className="text-[11px] text-[#6b655b] italic">None uploaded yet.</div>
                  ) : (
                    <ul className="space-y-1">
                      {(detail.portal_project_certificates || []).map((it) => (
                        <li key={it.id || it.public_id} className="flex items-center gap-2 text-[11px]">
                          <FileText size={11} className="text-[#234333]" />
                          <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-[#234333] hover:text-[#c78e42] truncate">{it.filename || "file"}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#6b655b] font-bold mb-1">Balance Sheets</div>
                  {(detail.portal_balance_sheets || []).length === 0 ? (
                    <div className="text-[11px] text-[#6b655b] italic">None uploaded yet.</div>
                  ) : (
                    <ul className="space-y-1">
                      {(detail.portal_balance_sheets || []).map((it) => (
                        <li key={it.id || it.public_id} className="flex items-center gap-2 text-[11px]">
                          <FileText size={11} className="text-[#234333]" />
                          <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-[#234333] hover:text-[#c78e42] truncate">{it.filename || "file"}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Section>
              <Section title="Admin Note" full>
                <textarea className="w-full border border-[#e5e0d8] rounded-sm px-3 py-2 text-xs min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)} disabled={!canEdit} data-testid="adm-vendor-note" placeholder="Add internal reviewer notes (visible only to admins)" />
              </Section>
            </div>
            {canEdit && (
              <div className="border-t border-[#e5e0d8] px-5 py-3 bg-[#f5f1e8] flex flex-wrap items-center justify-end gap-2">
                {detail.status !== "Under Review" && (
                  <button type="button" onClick={() => changeStatus({ ...detail, admin_note: note }, "Under Review")} disabled={busyId === detail.id} data-testid="adm-vendor-review" className="inline-flex items-center gap-1 bg-[#c78e42] hover:bg-[#a5875a] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm">
                    <RotateCcw size={12} /> Under Review
                  </button>
                )}
                {detail.status !== "Approved" && (
                  <button type="button" onClick={() => changeStatus({ ...detail, admin_note: note }, "Approved")} disabled={busyId === detail.id} data-testid="adm-vendor-approve" className="inline-flex items-center gap-1 bg-green-700 hover:bg-green-800 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm">
                    <CheckCircle2 size={12} /> Approve
                  </button>
                )}
                {detail.status !== "Rejected" && (
                  <button type="button" onClick={() => changeStatus({ ...detail, admin_note: note }, "Rejected")} disabled={busyId === detail.id} data-testid="adm-vendor-reject" className="inline-flex items-center gap-1 bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm">
                    <XCircle size={12} /> Reject
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">Delete vendor submission?</AlertDialogTitle>
            <AlertDialogDescription>All attached PDFs (Cloudinary) will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="adm-vendor-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#c78e42] font-bold mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2">
      <div className="text-[#6b655b] w-40 shrink-0">{k}</div>
      <div className="text-[#1c1a17] font-medium break-words">{v || "—"}</div>
    </div>
  );
}

function Attach({ label, url }) {
  if (!url) return (
    <div className="border border-dashed border-[#e5e0d8] rounded-sm p-2 text-center text-[10px] text-[#6b655b]">
      <div className="uppercase font-bold">{label}</div>
      <div>Not provided</div>
    </div>
  );
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="border border-[#e5e0d8] hover:border-[#234333] rounded-sm p-2 text-center block">
      <FileText size={16} className="mx-auto text-[#234333]" />
      <div className="text-[10px] font-bold uppercase mt-1 text-[#1c1a17]">{label}</div>
      <div className="text-[9px] text-[#c78e42]">View PDF</div>
    </a>
  );
}
